/**
 * @fileoverview "Plant something"'s state, reads and write (E25; owner, 30 Oct: "add a to do is one button, plant
 * something is another"), for PlantSomethingSheet to draw:
 *
 * - Which plant: the words typed, sent to the server's search (usePlantSearch, GET /plants/search, B121) and its rows
 *   drawn as sent; one tapped is the plant picked (Change goes back to the search, the words kept).
 * - The plan for the plant picked (GET /todo/own/plant/plan, read again when the bed changes): its name, the When the
 *   sheet starts on, "Grow recommends: …" and the check-mark line, all the server's. When a plan arrives, the When it
 *   names is picked (Pick a day with Grow's best day in the box when its days open later), unless she tapped a chip or
 *   the date since picking the plant.
 * - Already there (E26; owner, 30 Oct): when the plan says the bed chosen already has a plant of that kind, its
 *   choice ("Use that one" or "Add another") starts on the server's default each time a plan arrives (a new plant or
 *   bed), a tap changes it, and Add sends it. The plan without one sends nothing, as before.
 * - Which bed: the bed she came from, else her only bed, else none until she picks one (owner, 30 Oct); the garden's
 *   beds in use (useBeds, as the Add a plant form lists them).
 * - Add (POST /todo/own/plant): the plant at Planning and its to-do, together (after "Use that one", the to-do alone, on
 *   the plant already there). Once saved: the server's toast with its Undo, which takes back what the Add did (POST
 *   /todo/own/plant/undo with the server's key; owner, 30 Oct); Today and the plants read again behind it;
 *   the sheet closes and starts clean next time; the new to-do's key is handed to the page (its group shows it and
 *   scrolls to it). Add waits for a plant, a bed, the plan, and a day for Pick a day.
 * - Add with no bed (B133; owner, 30 Oct, 1 Nov): Add looks grey but still answers a tap (and Enter): save() with no bed
 *   chosen sends nothing and marks the bed missing (`bedMissing`, which the sheet draws as the bed select's error and
 *   "Choose a bed."); choosing a bed, or closing the sheet, clears it. Every other not-ready state (no plant, a past day,
 *   the plan loading, saving) does nothing, silently, and never says "Choose a plant.".
 *
 * A failure on our side (a 5xx, no connection) says the standard sentence and is reported (lib/serverFailure); Free's
 * plant limit says the server's words; any other refusal says todo.own.notSaved. The sheet keeps what was picked.
 *
 * Where: useOwnTodoSheet (Today, a bed's card).
 * API: GET /plants/search (usePlantSearch), GET /todo/own/plant/plan, POST /todo/own/plant, POST /todo/own/plant/undo
 * (api/plantSomething); GET /locations?status=1 (useBeds).
 * Words (en/es): todo.own.notSaved (the bed line, plants.form.missing.location, is drawn by the sheet).
 *
 * @module own-todos/usePlantSomething
 */

import React from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useAuth } from '@/shared/hooks/useAuth';
import { useBeds } from '@/shared/hooks/useBeds';
import { chosenGarden } from '@/shared/api/gardenMembers';
import { usePlantSearch, type PlantSearchReading } from '@/shared/components/plant-fields/usePlantSearch';
import type { PlantSearchRow } from '@/shared/api/plantSearch';
import type { TodoAsk } from '@/shared/api/todo';
import type { OwnWhen } from '@/shared/api/ownTodo';
import { addPlantSomething, getPlantPlan, undoPlantSomething, type AlreadyKey, type PlantPlan } from '@/shared/api/plantSomething';
import { serverFailureText } from '@/shared/lib/serverFailure';
import { QUERY_STALE_TIME_MS } from '@/config/queryClient';
import { sayTodo } from '@/features/today/lib/useTodoMarks';
import type { Location } from '@/shared/api/locations';

/** The first word of the plan's React Query key. @kept K1 */
export const PLANT_PLAN_QUERY_KEY = 'plant-something-plan';

/** The code the server gives when Free has no room for another plant (services/billing/freePlan). */
const FREE_PLANT_LIMIT = 'FREE_PLANT_LIMIT';

/** Where the sheet opens from: Today (no bed), or a bed's card (its bed). */
export interface PlantTarget {
  /** The bed she came from, or null from Today. */
  locationId: number | null;
}

/** What the sheet draws and does. */
export interface PlantSomething {
  /** The words in the search box. */
  query: string;
  setQuery: (next: string) => void;
  /** The server's answer to the words (rows, and whether it is the answer for what is typed now). */
  search: PlantSearchReading;
  /** The search row picked, or null while she is searching. */
  picked: PlantSearchRow | null;
  pick: (row: PlantSearchRow) => void;
  /** Back to the search, the words kept. */
  change: () => void;
  /** The server's plan for the plant picked; undefined while it is read. */
  plan: PlantPlan | undefined;
  /** Why the plan could not be read (for ServerFailureNote, which reports it), or null. */
  planError: unknown;
  when: OwnWhen;
  /** For Pick a day: "YYYY-MM-DD" ('' when none). */
  day: string;
  pickWhen: (next: OwnWhen) => void;
  setDay: (next: string) => void;
  /** E26: "Use that one" or "Add another", while the plan has a plant already there (the server's default first). */
  already: AlreadyKey;
  pickAlready: (next: AlreadyKey) => void;
  /** The garden's beds in use. */
  beds: Location[];
  locationId: number | null;
  setLocationId: (next: number | null) => void;
  /** Add is ready to send (it is drawn grey, aria-disabled, until it is; a tap while it is not ready sends nothing). */
  ready: boolean;
  /** She tapped Add with no bed chosen: the sheet marks the bed select and says "Choose a bed." until a bed is chosen. */
  bedMissing: boolean;
  busy: boolean;
  /** Why the last Add did not save, or null. */
  failed: string | null;
  /** Adds; resolves true once saved. */
  save: () => Promise<boolean>;
}

/**
 * "Plant something" for a page.
 *
 * @param args.open - The sheet is open (the reads run only then).
 * @param args.target - Where it opened from.
 * @param args.today - The garden's day, "YYYY-MM-DD".
 * @param args.ask - How the brief was asked for.
 * @param args.queryKey - The key the brief is kept under (read again once saved).
 * @param args.onSaved - The new to-do's key, for its group to show and scroll to.
 * @returns The sheet's state and actions.
 */
export function usePlantSomething({ open, target, today, ask, queryKey, onSaved }: {
  open: boolean;
  target: PlantTarget;
  today: string;
  ask: TodoAsk;
  queryKey: readonly unknown[];
  onSaved: (key: string | null) => void;
}): PlantSomething {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const { mysqlUser } = useAuth();
  const queryClient = useQueryClient();
  const gardenId = chosenGarden() ?? mysqlUser?.user_id ?? null;
  const [query, setQuery] = React.useState('');
  const [picked, setPicked] = React.useState<PlantSearchRow | null>(null);
  const [whenState, setWhen] = React.useState<OwnWhen>('today');
  const [dayState, setDay] = React.useState('');
  /** She tapped a chip or the date since picking the plant: a plan arriving no longer moves them. */
  const [touched, setTouched] = React.useState(false);
  const [locationId, setLocationId] = React.useState<number | null>(target.locationId);
  /** "Use that one" or "Add another" (E26): the server's default for each plan, until she taps the other. */
  const [already, setAlready] = React.useState<AlreadyKey>('add');
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState<string | null>(null);
  /** She tapped Add while no bed was chosen (bedMissing is this and still no bed). */
  const [bedTapped, setBedTapped] = React.useState(false);
  /** A write is on its way (a ref, so a second tap in the same moment cannot start another). */
  const inFlight = React.useRef(false);

  const search = usePlantSearch(picked ? '' : query, null, open && !picked);
  const { data: beds = [] } = useBeds(open);

  // A new opening from another place starts on that place's bed.
  const [targetSeen, setTargetSeen] = React.useState(target);
  if (target !== targetSeen) {
    setTargetSeen(target);
    setLocationId(target.locationId);
  }
  // A sheet that closes forgets a tap that found no bed.
  if (!open && bedTapped) setBedTapped(false);
  // Her only bed: nothing to ask.
  if (open && locationId === null && beds.length === 1) setLocationId(beds[0].id);

  const planQuery = useQuery({
    queryKey: [PLANT_PLAN_QUERY_KEY, gardenId, language, picked?.key ?? null, locationId],
    queryFn: () => getPlantPlan({ cropId: picked?.cropId ?? null, plantId: picked?.plantId ?? null, varietyId: picked?.varietyId ?? null }, locationId, language),
    enabled: open && picked !== null && Boolean(mysqlUser),
    staleTime: QUERY_STALE_TIME_MS,
  });
  const plan = planQuery.data;
  const planError: unknown = planQuery.error ?? null;

  // A plan arrived for the plant picked: its When, unless she chose one herself since; and the server's default for a
  // plant already in the bed, every time (a new plant or bed names another plant; owner, 30 Oct).
  const [planSeen, setPlanSeen] = React.useState<PlantPlan | undefined>(undefined);
  if (plan !== planSeen) {
    setPlanSeen(plan);
    setAlready(plan?.already?.choice ?? 'add');
    if (plan && !touched) {
      setWhen(plan.when);
      setDay(plan.day ?? '');
    }
  }

  // The When and day in force in this very render: a plan that has just arrived (not yet in state) already counts, so
  // a tap in the render the plan arrives in is judged on the plan's day, not the one before it.
  const planFresh = plan !== undefined && plan !== planSeen && !touched;
  const when = planFresh ? plan.when : whenState;
  const day = planFresh ? (plan.day ?? '') : dayState;

  /**
   * Picks a search row: the plan is read for it, and the When starts on Today until the plan says otherwise.
   *
   * @param row - The row tapped.
   */
  const pick = React.useCallback((row: PlantSearchRow) => {
    setPicked(row);
    setTouched(false);
    setWhen('today');
    setDay('');
    setFailed(null);
  }, []);
  /** Back to the search, the words kept. */
  const change = React.useCallback(() => {
    setPicked(null);
    setFailed(null);
  }, []);
  /**
   * Picks a When chip.
   *
   * @param next - The chip.
   */
  const pickWhen = React.useCallback((next: OwnWhen) => {
    setWhen(next);
    setTouched(true);
  }, []);
  /**
   * Sets the day for Pick a day.
   *
   * @param next - "YYYY-MM-DD".
   */
  const setDayTouched = React.useCallback((next: string) => {
    setDay(next);
    setTouched(true);
  }, []);

  /** The bed is missing and she has said so by tapping Add (a bed chosen since clears it). */
  const bedMissing = bedTapped && locationId === null;
  /** Add can send: a plant, a bed, the plan, a day that is not past, and no save under way. */
  const ready = picked !== null && locationId !== null && plan !== undefined && (when !== 'day' || day >= today) && !busy;

  /**
   * Takes an Add back, as the server's key says (the to-do and the plant; after "Use that one", the to-do alone or its
   * old When); says the server's toast, and reads Today, the plants and every plan again (a plan's "You already have" line goes stale after an Add or Undo; B128).
   *
   * @param key - The to-do's key.
   */
  const undo = React.useCallback(async (key: string) => {
    try {
      const { said } = await undoPlantSomething(key, ask);
      sayTodo(said ? { toast: said.toast, undo: null } : null, () => {}, true);
    } catch (error) {
      sayTodo({ toast: serverFailureText(error, 'Plant something undo') ?? t('todo.own.notSaved'), undo: null }, () => {}, true);
    } finally {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ['plants'] });
      void queryClient.invalidateQueries({ queryKey: [PLANT_PLAN_QUERY_KEY] });
    }
  }, [ask, queryClient, queryKey, t]);

  /**
   * Adds the plant and its to-do; resolves true once saved (the sheet then closes and starts clean). With no bed chosen
   * it sends nothing and marks the bed missing (B133), whatever else is missing; any other not-ready state returns false
   * without a word.
   *
   * @returns Whether it saved.
   */
  const save = React.useCallback(async (): Promise<boolean> => {
    if (!busy && !inFlight.current && locationId === null) {
      setBedTapped(true);
      return false;
    }
    if (!ready || !picked || locationId === null || inFlight.current) return false;
    inFlight.current = true;
    setBusy(true);
    setFailed(null);
    try {
      const { said } = await addPlantSomething({
        cropId: picked.cropId, plantId: picked.plantId, varietyId: picked.varietyId, locationId, when, ...(when === 'day' ? { day } : {}),
        ...(plan?.already ? { already } : {}),
      }, ask);
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ['plants'] });
      void queryClient.invalidateQueries({ queryKey: [PLANT_PLAN_QUERY_KEY] });
      const undoKey = said?.undo?.key ?? null;
      // The server's toast; its Undo is this sheet's (it takes back what the Add did), so the action named here is never read.
      sayTodo(said ? { toast: said.toast, undo: said.undo && undoKey ? { action: 'remove', key: undoKey, text: said.undo.text } : null } : null, () => {
        if (undoKey) void undo(undoKey);
      });
      onSaved(said?.openKey ?? null);
      setQuery('');
      setPicked(null);
      setWhen('today');
      setDay('');
      setTouched(false);
      setAlready('add');
      setBedTapped(false);
      setLocationId(target.locationId);
      return true;
    } catch (error) {
      const data = axios.isAxiosError(error) ? (error.response?.data as { code?: string; message?: string } | undefined) : undefined;
      setFailed(serverFailureText(error, 'Plant something add') ?? (data?.code === FREE_PLANT_LIMIT && data.message ? data.message : t('todo.own.notSaved')));
      return false;
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [already, ask, busy, day, locationId, onSaved, picked, plan, queryClient, queryKey, ready, t, target.locationId, undo, when]);

  return {
    query, setQuery, search, picked, pick, change, plan, planError, when, day, pickWhen, setDay: setDayTouched,
    already, pickAlready: setAlready, beds, locationId, setLocationId, ready, bedMissing, busy, failed, save,
  };
}
